import React, { useEffect, useMemo, useState } from 'react';
import { Navigation, Waves, Sun, Droplets, Map, Loader2, Wind, Mountain, ChevronDown } from 'lucide-react';
import { NowBlock } from '@/components/weather/NowBlock';
import { conditionView } from '@/lib/conditions';
import { api } from '@/lib/api';
import { codeIcon, codeLabel, windDirLabel, uvLabel, camName, seaLevel, seaLabel, SEA_LEVELS } from '@/lib/tenerife';
import { WindySheet } from '@/components/weather/WindySheet';

const T = {
  es: { feels: 'Sensación', wind: 'Viento', gusts: 'rachas', waves: 'Oleaje', period: 'periodo', sea: 'mar', uv: 'UV', humidity: 'Humedad', next: 'Próximas horas', week: '7 días', windy: 'Ver mapa Windy', source: 'Datos: Open-Meteo', error: 'No se pudo cargar la previsión', now: 'Ahora', today: 'Hoy', detail: 'Por horas y próximos 7 días', modelLabel: 'Previsión', calmaSource: 'Previsión: Calma Stays', models: 'Modelo ICON (DWD) vía Open‑Meteo', marine: ' · oleaje MeteoFrance MFWAM' },
  en: { feels: 'Feels like', wind: 'Wind', gusts: 'gusts', waves: 'Waves', period: 'period', sea: 'sea', uv: 'UV', humidity: 'Humidity', next: 'Next hours', week: '7 days', windy: 'Open Windy map', source: 'Data: Open-Meteo', error: 'Could not load forecast', now: 'Now', today: 'Today', detail: 'Hourly & next 7 days', modelLabel: 'Forecast', calmaSource: 'Forecast: Calma Stays', models: 'ICON model (DWD) via Open‑Meteo', marine: ' · waves MeteoFrance MFWAM' },
  de: { feels: 'Gefühlt', wind: 'Wind', gusts: 'Böen', waves: 'Wellen', period: 'Periode', sea: 'Meer', uv: 'UV', humidity: 'Feuchte', next: 'Nächste Stunden', week: '7 Tage', windy: 'Windy-Karte öffnen', source: 'Daten: Open-Meteo', error: 'Vorhersage nicht verfügbar', now: 'Jetzt', today: 'Heute', detail: 'Stündlich & nächste 7 Tage', modelLabel: 'Vorhersage', calmaSource: 'Vorhersage: Calma Stays', models: 'Modell ICON (DWD) via Open‑Meteo', marine: ' · Wellen MeteoFrance MFWAM' },
};
const LOCALE = { es: 'es-ES', en: 'en-GB', de: 'de-DE' };

const Stat = ({ icon: Icon, label, value, sub, testId, children }) => (
  <div className="flex items-start gap-2.5 min-w-0" data-testid={testId}>
    <div className="w-9 h-9 flex items-center justify-center bg-[#38B2AC]/10 text-[#38B2AC] flex-shrink-0">
      {children || <Icon size={18} />}
    </div>
    <div className="min-w-0">
      <p className="text-[11px] uppercase tracking-wide text-gray-500">{label}</p>
      <p className="font-bold text-[#1D1D1F] text-sm leading-tight">{value}</p>
      {sub && <p className="text-xs text-gray-500 truncate">{sub}</p>}
    </div>
  </div>
);

export const ForecastCard = ({ cam, language = 'es', now = new Date() }) => {
  const t = T[language] || T.es;
  const locale = LOCALE[language] || LOCALE.es;
  const [omData, setData] = useState(null);
  const [error, setError] = useState(false);
  const [windyOpen, setWindyOpen] = useState(false);
  const [point, setPoint] = useState('main');
  const alt = cam?.alt_forecast;
  // Pico tab: only the bot's `alt` block counts; the Cañadas reading never shows there
  const activeCondition = point === 'alt'
    ? (cam?.condition?.alt ? { ...cam.condition.alt, actualizado_en: cam.condition.actualizado_en, sin_pastilla: false } : null)
    : cam?.condition;
  const hasNow = Boolean(activeCondition && conditionView(activeCondition, now).state);
  const [detailOpen, setDetailOpen] = useState(!hasNow);

  useEffect(() => { setDetailOpen(!hasNow); }, [hasNow, cam?.id, point]);

  useEffect(() => { setPoint('main'); }, [cam?.id]);

  useEffect(() => {
    if (!cam?.id) return;
    let cancelled = false;
    setData(null); setError(false);
    api.getWebcamForecast(cam.id, point)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [cam?.id, point]);

  // Calma's own forecast (prevision) wins when fresher than 8 h; Open-Meteo stays as fallback (and for sea data)
  const prevision = activeCondition?.prevision;
  const previsionFresh = Boolean(prevision && activeCondition?.actualizado_en && (now - new Date(activeCondition.actualizado_en)) / 36e5 < 8);
  const data = useMemo(() => {
    if (!previsionFresh) return omData;
    const ICON_CODE = { sol: 0, sol_nubes: 2, nublado: 3, lluvia: 61, viento: 2, mar_malo: 3 };
    const isDay = (iso) => { const h = parseInt(new Intl.DateTimeFormat('en-GB', { timeZone: 'Atlantic/Canary', hour: '2-digit', hour12: false }).format(new Date(iso)), 10); return h >= 7 && h < 21 ? 1 : 0; };
    const h0 = prevision.horas[0];
    return {
      calma: true,
      current: { time: h0.hora, temp: h0.temp_c, feels_like: h0.sensacion_c ?? h0.temp_c, code: ICON_CODE[h0.icono] ?? 2, is_day: isDay(h0.hora), wind: h0.viento_kmh, wind_dir: null, gusts: h0.rachas_kmh, uv: null, text: h0.texto_corto?.[language] || h0.texto_corto?.es },
      hourly: prevision.horas.map((h) => ({ time: h.hora, temp: h.temp_c, code: ICON_CODE[h.icono] ?? 2, rain_prob: h.prob_lluvia, wind: h.viento_kmh, is_day: isDay(h.hora), text: h.texto_corto?.[language] || h.texto_corto?.es })),
      daily: prevision.dias.map((d) => ({ date: d.dia, code: ICON_CODE[d.icono] ?? 2, tmax: d.temp_max_c, tmin: d.temp_min_c, rain_prob: d.prob_lluvia, wind_max: d.viento_kmh, wave_max: null, text: d.texto_corto?.[language] || d.texto_corto?.es })),
      marine: omData?.marine, elevation: omData?.elevation, attribution_url: omData?.attribution_url || 'https://open-meteo.com/',
    };
  }, [previsionFresh, prevision, omData, language]);

  if (!cam) return null;
  const mainLabel = cam.main_forecast_name?.[`name_${language}`] || cam.main_forecast_name?.name_es || camName(cam, language);
  const name = point === 'alt' && alt ? (alt[`name_${language}`] || alt.name_es) : mainLabel;
  const fmtElev = (m) => String(Math.round(m)).replace(/\B(?=(\d{3})+(?!\d))/g, language === 'en' ? ',' : '.');
  const altLabel = point === 'alt' && alt ? `${t.modelLabel} · ${fmtElev(alt.elevation)} m` : null;

  const pointTabs = alt ? (
    <div className="flex bg-[#FDFBF7] border-b border-gray-100" role="tablist" data-testid="forecast-point-switch">
      {[['main', mainLabel, cam.elevation], ['alt', alt[`name_${language}`] || alt.name_es, alt.elevation]].map(([p, label, elev]) => (
        <button key={p} role="tab" aria-selected={point === p} onClick={() => setPoint(p)}
          className={`flex-1 py-2 text-sm font-semibold transition-colors duration-200 border-b-2 flex flex-col items-center leading-tight ${point === p ? 'border-[#38B2AC] text-[#1D1D1F]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          data-testid={`forecast-point-${p}`}>
          <span><Mountain size={14} className="inline mr-1.5 -mt-0.5" />{label}</span>
          {elev ? <span className="text-[11px] font-normal text-gray-400">{fmtElev(elev)} m</span> : null}
        </button>
      ))}
    </div>
  ) : null;

  const nowBlock = hasNow ? <NowBlock condition={activeCondition} language={language} now={now} label={altLabel} /> : null;
  const windyBtn = (
    <div className="px-5 pb-4 pt-1">
      <button onClick={() => setWindyOpen(true)} className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-full text-sm font-bold text-white bg-[#38B2AC] hover:bg-[#2C8F8A] active:scale-[0.99] transition-[background-color,transform] shadow-md" data-testid="windy-open-btn">
        <Map size={16} /> {t.windy}
      </button>
    </div>
  );
  const windySheet = <WindySheet open={windyOpen} onOpenChange={setWindyOpen} cam={point === 'alt' && alt ? { ...cam, latitude: alt.latitude, longitude: alt.longitude, elevation: alt.elevation } : cam} title={`Windy · ${name}`} />;

  if (error && !data) return <div className="bg-white shadow-md overflow-hidden" data-testid="forecast-card">{pointTabs}{nowBlock}<p className="p-6 text-sm text-gray-500" data-testid="forecast-error">{t.error}</p>{windyBtn}{windySheet}</div>;
  if (!data) return <div className="bg-white shadow-md overflow-hidden" data-testid="forecast-card">{pointTabs}{nowBlock}<div className="p-10 flex justify-center" data-testid="forecast-loading"><Loader2 className="animate-spin text-[#38B2AC]" /></div></div>;

  const c = data.current;
  const Icon = codeIcon(c.code, c.is_day);
  const hourFmt = (iso) => new Date(iso).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
  const dayFmt = (d, i) => (i === 0 ? t.today : new Date(`${d}T12:00`).toLocaleDateString(locale, { weekday: 'short', day: 'numeric' }));

  return (
    <div className="bg-white shadow-md overflow-hidden" data-testid="forecast-card">
      {pointTabs}
      {nowBlock}
      {hasNow && (
        <button onClick={() => setDetailOpen((v) => !v)} className="w-full flex items-center justify-between px-5 py-3 text-sm font-semibold text-[#1D1D1F] hover:bg-[#FDFBF7] transition-colors" aria-expanded={detailOpen} data-testid="forecast-detail-toggle">
          {t.detail} <ChevronDown size={16} className={`transition-transform ${detailOpen ? 'rotate-180' : ''}`} />
        </button>
      )}
      <div className={detailOpen ? '' : 'hidden'} data-testid="forecast-detail" style={{ transition: 'opacity 300ms' }}>
      <div className="p-5 pb-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="font-bold text-[#1D1D1F] truncate" data-testid="forecast-name">{name}</p>
            {altLabel && <p className="text-xs font-semibold text-[#2A8F8A]" data-testid="forecast-model-label">{altLabel}</p>}
            <p className="text-sm text-gray-500">{c.text || codeLabel(c.code, language)}{data.elevation != null && !cam.coastal && !altLabel ? ` · ${Math.round(data.elevation).toLocaleString(locale)} m` : ''}</p>
          </div>
          <Icon size={44} strokeWidth={1.5} className="text-[#38B2AC] flex-shrink-0" />
        </div>
        <div className="flex items-end gap-3 mt-2">
          <span className="text-6xl font-extrabold tracking-tight text-[#1D1D1F] leading-none" data-testid="forecast-temp">{Math.round(c.temp)}°</span>
          <span className="text-sm text-gray-500 pb-1.5">{t.feels} {Math.round(c.feels_like)}°</span>
        </div>
        {data.marine && (() => {
          const lv = seaLevel(data.marine.wave_height, data.marine.wave_period, c.wind);
          return lv ? (
            <span className="inline-flex items-center gap-2 mt-4 px-3 py-1.5 text-xs font-semibold text-white" style={{ backgroundColor: SEA_LEVELS[lv].color }} data-testid="forecast-sea-badge">
              <Waves size={13} /> {seaLabel(lv, language)}
            </span>
          ) : null;
        })()}
      </div>

      <div className="grid grid-cols-2 gap-x-4 gap-y-4 px-5 pb-5">
        <Stat label={t.wind} value={c.wind != null ? `${Math.round(c.wind)} km/h ${c.wind_dir != null ? windDirLabel(c.wind_dir, language) : ''}` : '—'} sub={c.gusts != null && data.marine ? `${t.gusts} ${Math.round(c.gusts)} km/h` : ''} testId="forecast-wind">
          {c.wind_dir != null ? <Navigation size={18} style={{ transform: `rotate(${(c.wind_dir + 180) % 360}deg)`, transition: 'transform 300ms' }} /> : <Wind size={18} />}
        </Stat>
        {data.marine ? (
          <Stat icon={Waves} label={t.waves} value={`${data.marine.wave_height.toFixed(1)} m`} sub={`${t.period} ${Math.round(data.marine.wave_period)} s${data.marine.sea_temp != null ? ` · ${t.sea} ${Math.round(data.marine.sea_temp)}°` : ''}`} testId="forecast-waves" />
        ) : (
          <Stat icon={Wind} label={t.gusts} value={c.gusts != null ? `${Math.round(c.gusts)} km/h` : '—'} testId="forecast-gusts" />
        )}
        <Stat icon={Sun} label={t.uv} value={c.uv != null ? `${Math.round(c.uv)} · ${uvLabel(c.uv, language)}` : '—'} testId="forecast-uv" />
        <Stat icon={Droplets} label={t.humidity} value={c.humidity != null ? `${c.humidity}%` : '—'} testId="forecast-humidity" />
      </div>

      <div className="border-t border-gray-100 pt-4">
        <p className="px-5 text-[11px] uppercase tracking-wide text-gray-500 mb-2">{t.next}</p>
        <div className="flex gap-2 overflow-x-auto px-5 pb-4 scrollbar-hide snap-x" data-testid="forecast-hourly">
          {data.hourly.map((h, i) => {
            const HI = codeIcon(h.code, h.is_day);
            return (
              <div key={h.time} className="flex flex-col items-center gap-1.5 min-w-[60px] py-2.5 bg-[#FDFBF7] snap-start" data-testid={`forecast-hour-${i}`}>
                <span className="text-[11px] text-gray-500">{i === 0 ? t.now : hourFmt(h.time)}</span>
                <HI size={20} strokeWidth={1.6} className="text-[#38B2AC]" />
                <span className="text-sm font-bold text-[#1D1D1F]">{Math.round(h.temp)}°</span>
                <span className="text-[10px] text-gray-400">{Math.round(h.wind)} km/h</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="border-t border-gray-100 pt-4 px-5 pb-4">
        <p className="text-[11px] uppercase tracking-wide text-gray-500 mb-1">{t.week}</p>
        <div data-testid="forecast-daily">
          {data.daily.map((d, i) => {
            const DI = codeIcon(d.code, 1);
            return (
              <div key={d.date} className="flex items-center gap-3 py-2 border-b border-gray-50 last:border-0" data-testid={`forecast-day-${i}`}>
                <span className="w-16 text-sm font-medium text-[#1D1D1F] capitalize">{dayFmt(d.date, i)}</span>
                <DI size={18} strokeWidth={1.6} className="text-[#38B2AC]" />
                <span className="text-xs text-gray-400 w-10">{d.rain_prob != null && d.rain_prob > 0 ? `${d.rain_prob}%` : ''}</span>
                {d.wave_max != null && <span className="text-xs text-gray-400 flex items-center gap-1"><Waves size={12} />{d.wave_max.toFixed(1)} m</span>}
                <span className="ml-auto text-sm text-gray-400">{Math.round(d.tmin)}°</span>
                <span className="text-sm font-bold text-[#1D1D1F] w-8 text-right">{Math.round(d.tmax)}°</span>
                {d.text && <span className="text-[11px] text-gray-500 leading-tight line-clamp-2 max-w-[96px]" title={d.text}>{d.text}</span>}
              </div>
            );
          })}
        </div>
      </div>

      </div>

      {windyBtn}
      <div className="px-5 py-2.5 bg-[#FDFBF7] border-t border-gray-100">
        <a href={data.attribution_url} target="_blank" rel="noopener noreferrer" className="text-[11px] text-gray-400 hover:text-gray-600 leading-tight" data-testid="forecast-attribution">{data.calma ? `${t.calmaSource}${data.marine ? t.marine : ''}` : `${t.models}${data.marine ? t.marine : ''}`}</a>
      </div>
      {windySheet}
    </div>
  );
};

export default ForecastCard;
